import { useEffect, useState } from "react";
import { useApiFetch } from "../../api/client";
import { useAppUser } from "../../app/AppUserContext";
import { useConfirmDialog } from "../../components/ConfirmDialog";

interface TdsFileListItem {
  intakeFileId: number;
  batchGuid: string;
  originalFileName: string;
  fileSizeBytes: number;
  uploadedUtc: string;
  createdByAppUserId: number;
  status: "Committed" | "Discarded";
}

const PAGE_SIZE = 50;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function TdsDetailsTab() {
  const apiFetch = useApiFetch();
  const { appRole, appUserId } = useAppUser();
  const canManage = (createdByAppUserId: number) =>
    appRole === "Admin" || (appRole === "Uploader" && createdByAppUserId === appUserId);

  const [items, setItems] = useState<TdsFileListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const { confirm, dialog } = useConfirmDialog();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (search.trim()) params.set("search", search.trim());
      const res = await apiFetch(`tds/files?${params.toString()}`);
      const page: { items: TdsFileListItem[]; totalCount: number } = await res.json();
      setItems(page.items);
      setTotalCount(page.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, offset]);

  const download = async (intakeFileId: number) => {
    try {
      const res = await apiFetch(`tds/files/${intakeFileId}`);
      const file: { readUrl: string | null } = await res.json();
      if (file.readUrl) window.open(file.readUrl, "_blank", "noopener");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const remove = async (item: TdsFileListItem) => {
    const result = await confirm({
      title: "Delete file",
      message: `Delete "${item.originalFileName}"? This cannot be undone from here.`,
    });
    if (result === null) return;
    try {
      await apiFetch(`tds/batches/${item.batchGuid}`, { method: "DELETE" });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const selectable = items.filter((f) => canManage(f.createdByAppUserId));
  const allSelected = selectable.length > 0 && selectable.every((f) => selectedIds.has(f.intakeFileId));

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      selectable.forEach((f) => next.add(f.intakeFileId));
      return next;
    });
  };

  const bulkDelete = async () => {
    const selectedItems = items.filter((f) => selectedIds.has(f.intakeFileId));
    if (!selectedItems.length) return;

    const result = await confirm({
      title: "Delete selected files",
      message: `Delete ${selectedItems.length} technical data sheet(s)? This cannot be undone from here.`,
    });
    if (result === null) return;

    const confirmAgain = await confirm({
      title: "Are you sure?",
      message: `This cannot be undone. Confirm deleting ${selectedItems.length} file(s)?`,
      confirmLabel: "Yes, delete",
    });
    if (confirmAgain === null) return;

    try {
      await Promise.all(
        selectedItems.map((f) => apiFetch(`tds/batches/${f.batchGuid}`, { method: "DELETE" })),
      );
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const pageStart = totalCount === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, totalCount);

  return (
    <>
    <section className="lane on">
      <h2>TDS Details</h2>
      <p className="lede">Every technical data sheet on file.</p>

      <div className="sec">
        <div className="card">
          <input
            className="ctl"
            placeholder="Filter by file name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
          />
        </div>
      </div>

      {error && (
        <div className="sec"><div className="gate narrow"><span className="msg" style={{ color: "var(--salmon)" }}>{error}</span></div></div>
      )}

      {selectedIds.size > 0 && (
        <div className="sec">
          <div className="card" style={{ padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>{selectedIds.size} selected</span>
            <button className="btn danger" onClick={bulkDelete}>
              <span className="material-symbols-sharp">delete_forever</span>Delete Selected
            </button>
          </div>
        </div>
      )}

      <div className="sec">
        <div className="card" style={{ padding: 0, overflow: "auto", maxWidth: "100%" }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 1 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectable.length === 0} />
                </th>
                <th>File</th>
                <th>Uploaded</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4}>Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={4}>No technical data sheets on file.</td></tr>}
              {items.map((f) => (
                <tr key={f.intakeFileId}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(f.intakeFileId)}
                      disabled={!canManage(f.createdByAppUserId)}
                      onChange={() => toggleOne(f.intakeFileId)}
                    />
                  </td>
                  <td>{f.originalFileName}<div style={{ color: "var(--grey)", fontSize: 11.5 }}>{formatBytes(f.fileSizeBytes)}</div></td>
                  <td>{new Date(f.uploadedUtc).toLocaleString()}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn ghost" onClick={() => download(f.intakeFileId)}>
                      <span className="material-symbols-sharp">download</span>Download
                    </button>
                    <button
                      className="btn ghost"
                      style={{ marginLeft: 6 }}
                      disabled={!canManage(f.createdByAppUserId)}
                      title={!canManage(f.createdByAppUserId) ? "You can only delete files you uploaded, unless you're an Admin" : undefined}
                      onClick={() => remove(f)}
                    >
                      <span className="material-symbols-sharp">delete</span>Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <div className="gate">
            <span className="msg">Showing {pageStart}–{pageEnd} of {totalCount}</span>
            <button className="btn ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              <span className="material-symbols-sharp">chevron_left</span>Previous
            </button>
            <button className="btn ghost" disabled={pageEnd >= totalCount} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next<span className="material-symbols-sharp">chevron_right</span>
            </button>
          </div>
        )}
      </div>
    </section>
    {dialog}
    </>
  );
}
